import React, { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Icon, Popup } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import InfitiveScrollDropown from '~/components/InfitiveScrollDropown'
import Layout from '~/components/Layout'
import { RouteTypes } from '~/routes/constants'
import { El } from '~/components/Layout/style'
import { useNavigate, useParams } from 'react-router-dom'
import ShowMoreDialog from './ShomMoreDialog'
import { handleErrors } from '~/utils/helpers'
import { IndividualFiltersService } from '~/api/services/individualFilters'
import { SelectedContent, SelectedWrapper } from '../style'
import notify from '~/components/Notify'
import { useMutation } from 'react-query'
import { FarmersService } from '~/api/services/farmers/index'

const MIN_DISTRICTS_TO_DISPLAY = 8

const CreateEditGeoFilter:React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isBusy, setIsBusy] = useState(false)

  const { control, handleSubmit, setValue, reset } = useForm({
    mode: 'onSubmit'
  })

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'districts',
    keyName: 'key'
  })

  const {
    data: districts,
    isLoading: isLoadingDistricts,
    mutate: getDistrictList
  } = useMutation(
    'individual-filter-districts',
    FarmersService.getDistrictList,
    {
      onError: (error: any) => {
        handleErrors(error)
      }
    }
  );

  useEffect(() => {
    if(id) {
      fetchData()
    } else {
      getDistrictList({ isMain: 1, selected: [] })
    }
  }, [id])

  async function fetchData() {
    setIsBusy(true)
    try {
      const response = await IndividualFiltersService.filtersGeographicEdit(id)
      getDistrictList({ isMain: 1, selected: response.data?.districts?.map(district => district.id) })
      reset({
        ...response.data,
        districts: response.data.districts.map(district => ({ ...district, name: `${district.name} (${district.zip})` }))
      })
    } catch(error) {
      handleErrors(error)
    }finally {
      setIsBusy(false)
    }
  }

  async function onSubmit(data) {
    if(data.districts?.length === 0) {
      notify(t('Please select districts'), 'error')
      return
    }
    setIsBusy(true)
    try {
      if(id) {
        await IndividualFiltersService.filtersGeographicUpdate(id, data)
        notify(t('Successfully updated geographical filter'), 'success')
      } else {
        await IndividualFiltersService.filtersGeographicStore(data)
        notify(t('Successfully created geographical filter'), 'success')
      }
      navigate(RouteTypes.DAIRY_FACTORY_PROFILE_INDIVIDUAL_FILTERS_GEOGRAPHICAL)
    } catch(error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  function renderDistricts() {
    const displayedDistricts = fields.slice(0, MIN_DISTRICTS_TO_DISPLAY)
    return displayedDistricts.map((districts, index) => {
      return <div key={districts.key} style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <SelectedContent $defaultCursor>
          <div style={{ display: 'flex' }}>
            <div
              className='title'
            >{`${index + 1}. ${districts['name']}`}
            </div>
          </div>
        </SelectedContent>
        <Icon
          name='trash'
          onClick={() => remove(index)}
          color='red'
          style={{ cursor: 'pointer' }}
        />
      </div>
    })
  }

  function renderForm() {
    return <>
      <FormFields.Input
        control={control}
        name={'name'}
        placeholder={t('Enter name')}
        rules={{
          required: t('Please enter name!'),
          maxLength: {
            value: 150,
            message: t('Max char. length is 150!')
          }
        }}
        label={t('Name')}
      />
      <SelectedWrapper>
        {renderDistricts()}
        {fields.length > MIN_DISTRICTS_TO_DISPLAY && <Popup
          content={t('Show more')}
          trigger={<Icon
            onClick={() => setIsDialogOpen(true)}
            name='ellipsis horizontal'
            size='large'
            style={{
              cursor: 'pointer',
              fontSize: '1.1em',
              boxShadow: '0 0 0 .1em rgba(0,0,0,0.3) inset' }}
            circular
          />}
        /> }
      </SelectedWrapper>
      <div style={{ minHeight: '17em' }}>
        <InfitiveScrollDropown
          control={control}
          scrolling={true}
          selectOnBlur={false}
          name={''}
          id={'dropdown-geographical-menu-create'}
          label={t('Districts')}
          maxHeightMenu={'11em'}
          useDistrictService
          loading={isLoadingDistricts}
          values={fields}
          disabled={isLoadingDistricts}
          value={''}
          onChange={(e, district) => {
            prepend({
              ...district,
              id: district.value,
              name: district.text
            })
          }}
          closeOnChange={false}
          setValue={setValue}
          placeholder={t('Select district')}
          options={districts?.data?.map(district => ({ value: district.id.toString(), text: `${district.name} (${district.zip})` })) || []}
        />
      </div>
    </>
  }

  function renderActions() {
    return <>
      <El.PageHeaderActionButton type='submit'
        disabled={isBusy}
        loading={isBusy}>{t('Save')}</El.PageHeaderActionButton>
      <El.PageHeaderActionButton
        type='button'
        onClick={() => navigate(RouteTypes.DAIRY_FACTORY_PROFILE_INDIVIDUAL_FILTERS_GEOGRAPHICAL)}
      >{t('Cancel')}</El.PageHeaderActionButton>
    </>
  }

  function renderShowMoreDialog() {
    return <ShowMoreDialog
      isDialogOpen={isDialogOpen}
      handleClose={() => setIsDialogOpen(false)}
      remove={remove}
      districts={fields}
    />
  }

  return <Layout
    title={id ? t('Edit geographical filter') : t('Create geographical filter')}
    onSubmit={handleSubmit(onSubmit)}
    fontSizeHeader={'18px'}
    childContainerSize={'300px'}
    isBusy={isBusy}
    actions={renderActions()}
  >
    {renderShowMoreDialog()}
    {renderForm()}
  </Layout>
}
export default CreateEditGeoFilter